/**
 * Chess Error Handler
 * 
 * Handles errors and recovery for chess games including:
 * - Move validation errors
 * - Connection errors
 * - Corrupted save data
 * - AI calculation timeouts
 * - Multiplayer move validation
 */

import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService, NotificationType } from '../../services/NotificationService';
import { GameState } from '../types';

export enum ChessErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  AI_TIMEOUT = 'AI_TIMEOUT',
  MULTIPLAYER_VALIDATION = 'MULTIPLAYER_VALIDATION',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  SYNC_ERROR = 'SYNC_ERROR',
  UNKNOWN = 'UNKNOWN'
}

export interface ChessError {
  type: ChessErrorType;
  message: string;
  details?: any;
  timestamp: number;
  recoverable: boolean;
}

export class ChessErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: ChessError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  /**
   * Handle a chess-specific error
   */
  handleError(
    type: ChessErrorType,
    message: string,
    details?: any,
    showNotification: boolean = true
  ): ChessError {
    const error: ChessError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type)
    };

    // Log error
    this.logError(error);

    // Report to error service (just log to console for now)
    console.error(`Chess Error [${type}]:`, message, details);

    // Show notification if requested
    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  /**
   * Handle invalid move error
   */
  handleInvalidMove(from: any, to: any, reason: string): ChessError {
    return this.handleError(
      ChessErrorType.INVALID_MOVE,
      `Invalid move: ${reason}`,
      { from, to, reason }
    );
  }

  /**
   * Handle connection error
   */
  handleConnectionError(error: any): ChessError {
    return this.handleError(
      ChessErrorType.CONNECTION_ERROR,
      'Connection lost. Attempting to reconnect...',
      error,
      true
    );
  }

  /**
   * Handle corrupted save data
   */
  handleCorruptedData(dataType: string, error: any): ChessError {
    return this.handleError(
      ChessErrorType.CORRUPTED_DATA,
      `Corrupted ${dataType} data detected. Starting fresh game.`,
      error,
      true
    );
  }

  /**
   * Handle AI timeout
   */
  handleAITimeout(difficulty: string, timeLimit: number): ChessError {
    return this.handleError(
      ChessErrorType.AI_TIMEOUT,
      `AI calculation timed out after ${timeLimit}ms`,
      { difficulty, timeLimit },
      true
    );
  }

  /**
   * Handle multiplayer validation error
   */
  handleMultiplayerValidation(move: any, reason: string): ChessError {
    return this.handleError(
      ChessErrorType.MULTIPLAYER_VALIDATION,
      `Invalid move from opponent: ${reason}`,
      { move, reason },
      true
    );
  }

  /**
   * Handle save error
   */
  handleSaveError(gameId: string, error: any): ChessError {
    return this.handleError(
      ChessErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  /**
   * Handle load error
   */
  handleLoadError(gameId: string, error: any): ChessError {
    return this.handleError(
      ChessErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  /**
   * Handle sync error
   */
  handleSyncError(error: any): ChessError {
    return this.handleError(
      ChessErrorType.SYNC_ERROR,
      'Failed to sync game data. Will retry later.',
      error,
      false
    );
  }

  /**
   * Validate game state
   */
  validateGameState(gameState: any): { valid: boolean; error?: ChessError } {
    try {
      // Check required properties
      if (!gameState) {
        return {
          valid: false,
          error: this.handleCorruptedData('game state', 'Game state is null or undefined')
        };
      }

      if (!gameState.board || !Array.isArray(gameState.board)) {
        return {
          valid: false,
          error: this.handleCorruptedData('game state', 'Invalid board structure')
        };
      }

      if (!gameState.currentPlayer || (gameState.currentPlayer !== 'white' && gameState.currentPlayer !== 'black')) {
        return {
          valid: false,
          error: this.handleCorruptedData('game state', 'Invalid current player')
        };
      }

      if (!gameState.gameStatus) {
        return {
          valid: false,
          error: this.handleCorruptedData('game state', 'Missing game status')
        };
      }

      return { valid: true };
    } catch (error) {
      return {
        valid: false,
        error: this.handleCorruptedData('game state', error)
      };
    }
  }

  /**
   * Attempt to recover from error
   */
  attemptRecovery(error: ChessError, gameState?: GameState): GameState | null {
    if (!error.recoverable) {
      return null;
    }

    switch (error.type) {
      case ChessErrorType.CORRUPTED_DATA:
        // Return a fresh game state
        return this.createFreshGameState();

      case ChessErrorType.CONNECTION_ERROR:
        // Game state should be preserved
        return gameState || null;

      case ChessErrorType.SAVE_ERROR:
      case ChessErrorType.LOAD_ERROR:
        // Try to use provided game state or create fresh
        return gameState || this.createFreshGameState();

      default:
        return gameState || null;
    }
  }

  /**
   * Create a fresh game state for recovery
   */
  private createFreshGameState(): GameState | null {
    // This would typically call ChessEngine.initializeGame()
    // For now, return null to indicate caller should initialize
    return null;
  }

  /**
   * Check if error type is recoverable
   */
  private isRecoverable(type: ChessErrorType): boolean {
    switch (type) {
      case ChessErrorType.INVALID_MOVE:
      case ChessErrorType.CONNECTION_ERROR:
      case ChessErrorType.CORRUPTED_DATA:
      case ChessErrorType.AI_TIMEOUT:
      case ChessErrorType.SAVE_ERROR:
      case ChessErrorType.LOAD_ERROR:
      case ChessErrorType.SYNC_ERROR:
        return true;
      
      case ChessErrorType.MULTIPLAYER_VALIDATION:
        return false; // Requires manual intervention
      
      default:
        return false;
    }
  }

  /**
   * Log error to internal log
   */
  private logError(error: ChessError): void {
    this.errorLog.push(error);

    // Keep log size manageable
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  /**
   * Show error notification to user
   */
  private showErrorNotification(error: ChessError): void {
    const notificationType = error.recoverable ? NotificationType.WARNING : NotificationType.ERROR;

    this.notificationService.addNotification({
      type: notificationType,
      title: 'Chess Error',
      message: error.message,
      duration: 5000
    });
  }

  /**
   * Get error log
   */
  getErrorLog(): ChessError[] {
    return [...this.errorLog];
  }

  /**
   * Get recent errors
   */
  getRecentErrors(count: number = 10): ChessError[] {
    return this.errorLog.slice(-count);
  }

  /**
   * Clear error log
   */
  clearErrorLog(): void {
    this.errorLog = [];
  }

  /**
   * Get error count by type
   */
  getErrorCountByType(type: ChessErrorType): number {
    return this.errorLog.filter(e => e.type === type).length;
  }

  /**
   * Check if there are critical errors
   */
  hasCriticalErrors(): boolean {
    return this.errorLog.some(e => !e.recoverable);
  }

  /**
   * Export error log
   */
  exportErrorLog(): string {
    return JSON.stringify(this.errorLog, null, 2);
  }
}
